'use client';
/**
 * `useSwapTrade` — SDK-owned outcome→outcome swap mutation.
 *
 * A swap moves a position from one outcome to another. It always indexes as a
 * distinct SELL event (of the `from` outcome) AND BUY event (of the `to`
 * outcome) so PnL is attributed correctly — never as a single opaque "swap".
 *
 * Two submission strategies, tried in order:
 *
 *  1. ONE atomic tx (`buildSwap` = `[sell(from), buy(to)]`). Best UX: a single
 *     signature / single Turnkey round-trip, and atomic (both legs land or both
 *     revert — no partial fill). The buy budget is sized from the sell's floored
 *     proceeds, so the sell always covers the buy within the tx.
 *
 *  2. TWO sequential txs (`buildSell` → await landing → `buildBuy`) — the
 *     fallback when the one-tx form exceeds Solana's 1232-byte packet limit
 *     (`TX_TOO_LARGE`, raised by the submit pipeline BEFORE anything is sent, so
 *     the one-tx attempt provably did not land — the fallback can never
 *     double-execute). Two round-trips, and NOT atomic: if the sell lands but
 *     the buy fails the user is left holding USDC. That partial fill is surfaced
 *     truthfully (never as "swap complete") with only the `from`-outcome patched
 *     down, so the UI matches chain reality and the user can retry the buy.
 *
 * Strategy 1 auto-engages once a populated Morfi ALT compacts the one-tx form
 * under the limit; until then every swap transparently uses strategy 2.
 *
 * Each leg arms the NATS early-confirm watch (success fires the instant the
 * indexer publishes the leg's trade event, before the slower Turnkey poll) and
 * mirrors the buy/sell hooks' landing semantics: a definitive on-chain revert
 * (`TX_CONFIRM_FAILED`) is authoritative, an ambiguous post-send error waits
 * `LANDING_GRACE_MS` for the indexer's ground-truth event.
 */
import { BN } from '@coral-xyz/anchor';
import { useRef } from 'react';
import { safeTxErrorCode } from '../../../tx-engine/errors/errors.js';
import { createTradeConfirm, isAmbiguousLandingError, LANDING_GRACE_MS, } from '../../../tx-engine/watch/trade-confirm.js';
import { reconcileThesisReplyEligibility } from './helpers/reconcile-thesis-reply-eligibility.js';
import { reconcileSettledTradeEntities, reconcileTradeEntityTargets, } from './helpers/reconcile-trade-entities.js';
import { trackTxException, trackTxFailure, trackTxSuccess } from './helpers/telemetry.js';
import { showCancelledToast, showFailureToast, showSubmittingToast, showSuccessToast, } from './helpers/toasts.js';
import { generateTraceId, nextTxNumber } from './helpers/trace.js';
import { useTradingHostContext } from './host-context.js';
import { useTradeMutation } from './use-trade-mutation.js';
export function useSwapTrade() {
    const deps = useTradingHostContext();
    const abortControllerRef = useRef(null);
    const mutation = useTradeMutation(deps, 'swap', {
        mutationFn: async (params, traceId, deps) => {
            if (params.signingDeps === undefined) {
                throw new Error('signingDeps is required for swap transactions');
            }
            const signingDeps = params.signingDeps;
            // Capture before submission: NATS/REST may publish the trade before success fires.
            const updatePosition = deps.optimisticPositions?.prepare?.() ?? deps.optimisticPositions?.upsert;
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            const startedAt = Date.now();
            const tradeEntityTargets = new Map();
            const recordTradeEntityTarget = (signature, expectedEventCount) => {
                if (signature === null || signature.length === 0)
                    return;
                tradeEntityTargets.set(signature, Math.max(tradeEntityTargets.get(signature) ?? 0, expectedEventCount));
            };
            const currentTradeEntityTargets = () => [...tradeEntityTargets].map(([signature, expectedEventCount]) => ({
                signature,
                expectedEventCount,
            }));
            const withTradeEntityTargets = (result) => {
                const targets = currentTradeEntityTargets();
                return targets.length === 0 ? result : { ...result, tradeEntityTargets: targets };
            };
            const txNum = nextTxNumber('swap');
            deps.txSubmission.startSubmission(traceId, 'swap');
            showSubmittingToast(deps, traceId, 'swap', txNum, '', 1, 3, abortController);
            const makeSwapCtx = () => ({
                traceId,
                instruction: 'swap',
                walletType: params.activeWallet.type,
                walletAddress: params.activeWallet.address,
                marketAddress: params.marketAddress,
                outcomeIndex: params.fromOutcomeIndex,
                latencyMs: Date.now() - startedAt,
                // Revenue attribution: notional = sell-leg gross proceeds (pre-fee).
                tradeNotionalD6: params.sellGrossQuoteOutputD6.toString(),
                ...(params.platformFeeD6 !== undefined ? { platformFeeD6: params.platformFeeD6 } : {}),
            });
            // Cross-cutting flags read by the catch/finally. `swapCompleted` = the buy
            // (or the atomic one-tx) landed; `sellLanded` = the fallback's sell landed
            // (so a subsequent throw is a partial fill, not a clean failure).
            let swapCompleted = false;
            let sellLanded = false;
            let completedSignature = null;
            const confirms = [];
            // A throw before a leg's `submitTx` is a build failure (RPC reads,
            // account loading); the engine never saw that leg, so it must not be
            // reported as an unknown submission error.
            let stage = 'build';
            try {
                const { buildSwap, buildSell, buildBuy } = await import('@morfi/contract-sdk');
                const { PublicKey } = await import('@solana/web3.js');
                deps.txSubmission.updateStage(traceId, 'signing');
                const connection = deps.getConnection();
                const market = new PublicKey(params.marketAddress);
                const user = new PublicKey(params.activeWallet.address);
                const minUsdcOut = new BN(0);
                const minTokensOut = new BN(0);
                // ── success / progress side-effects (each fires AT MOST ONCE) ──────────
                const reShowSwapSuccess = (signature) => {
                    deps.txSubmission.setSignature(traceId, signature);
                    showSuccessToast(deps, traceId, 'swap', txNum, '', signature, params.marketAddress, params.activeWallet.address);
                };
                // Optimistic positions-list upserts for each swap leg (bonding-curve,
                // so `isAmm: false`). Shared by the atomic + fallback-leg handlers.
                const upsertFromSell = () => updatePosition?.({
                    walletAddress: params.activeWallet.address,
                    market: params.marketAddress,
                    outcomeIndex: params.fromOutcomeIndex,
                    outcomeMint: params.fromOutcomeMint,
                    tokenDeltaD6: -BigInt(params.tokensD6.toString()),
                    quoteD6: BigInt(params.expectedSellNetUsdcOutD6.toString()),
                    grossQuoteD6: BigInt(params.sellGrossQuoteOutputD6.toString()),
                    penaltyD6: BigInt(params.sellPenaltyFeeD6?.toString() ?? 0),
                    walletDebitD6: null,
                    isAmm: false,
                });
                const upsertToBuy = () => updatePosition?.({
                    walletAddress: params.activeWallet.address,
                    market: params.marketAddress,
                    outcomeIndex: params.toOutcomeIndex,
                    outcomeMint: params.toOutcomeMint,
                    tokenDeltaD6: BigInt(params.estimatedTokensOutD6.toString()),
                    quoteD6: BigInt(params.buyAmountInD6.toString()),
                    grossQuoteD6: BigInt(params.buyGrossQuoteInputD6.toString()),
                    // The swap's buy leg never pays the conviction penalty.
                    penaltyD6: BigInt(0),
                    walletDebitD6: BigInt(params.buyWalletDebitD6.toString()),
                    isAmm: false,
                });
                // The atomic one-tx landed: both legs are done. Patch both balances.
                const fireSwapSuccess = (signature) => {
                    recordTradeEntityTarget(signature, 2);
                    trackTxSuccess(deps, makeSwapCtx(), signature ?? '');
                    swapCompleted = true;
                    completedSignature = signature;
                    deps.txSubmission.setSignature(traceId, signature ?? '');
                    deps.txSubmission.updateStage(traceId, 'confirmed');
                    showSuccessToast(deps, traceId, 'swap', txNum, '', signature ?? '', params.marketAddress, params.activeWallet.address);
                    deps.optimisticBalance?.patch({
                        walletAddress: params.activeWallet.address,
                        outcomeMint: params.fromOutcomeMint,
                        rawDeltaD6: -BigInt(params.tokensD6.toString()),
                    });
                    deps.optimisticBalance?.patch({
                        walletAddress: params.activeWallet.address,
                        outcomeMint: params.toOutcomeMint,
                        rawDeltaD6: BigInt(params.estimatedTokensOutD6.toString()),
                    });
                    // Optimistically reflect both legs in the positions lists (swaps are
                    // bonding-curve): shrink the `from` outcome, open/grow the `to`.
                    upsertFromSell();
                    upsertToBuy();
                };
                // Fallback SELL leg landed: the user has sold the `from` outcome (USDC
                // credited on chain). Patch ONLY the from-outcome down — the swap is not
                // complete until the buy lands, so NO success toast fires here.
                const onSellLanded = (signature) => {
                    recordTradeEntityTarget(signature, 1);
                    sellLanded = true;
                    deps.optimisticBalance?.patch({
                        walletAddress: params.activeWallet.address,
                        outcomeMint: params.fromOutcomeMint,
                        rawDeltaD6: -BigInt(params.tokensD6.toString()),
                    });
                    upsertFromSell();
                    deps.txSubmission.updateStage(traceId, 'submitted');
                };
                // Fallback BUY leg landed → the swap is now complete. Patch the
                // to-outcome up and show the swap-success toast.
                const onSwapComplete = (signature) => {
                    recordTradeEntityTarget(signature, 1);
                    trackTxSuccess(deps, makeSwapCtx(), signature ?? '');
                    swapCompleted = true;
                    completedSignature = signature;
                    deps.txSubmission.setSignature(traceId, signature ?? '');
                    deps.txSubmission.updateStage(traceId, 'confirmed');
                    showSuccessToast(deps, traceId, 'swap', txNum, '', signature ?? '', params.marketAddress, params.activeWallet.address);
                    deps.optimisticBalance?.patch({
                        walletAddress: params.activeWallet.address,
                        outcomeMint: params.toOutcomeMint,
                        rawDeltaD6: BigInt(params.estimatedTokensOutD6.toString()),
                    });
                    upsertToBuy();
                };
                // ── one leg: arm NATS watch → submit → resolve landing ─────────────────
                const executeLeg = async (leg) => {
                    const confirm = createTradeConfirm({
                        wallet: params.activeWallet.address,
                        market: params.marketAddress,
                        outcomeIdx: leg.outcomeIndex,
                        side: leg.side,
                        venue: 'bc',
                        submitStartMs: leg.startedAtMs,
                        debug: (msg, ctx) => deps.logger.info(`[tx] ⚡ ${msg}`, { traceId: leg.legTraceId, ...ctx }),
                    }, { fireSuccess: leg.onLanded, reconcileSignature: leg.onReconcileSig });
                    confirms.push(confirm);
                    stage = 'build';
                    const built = await leg.build();
                    stage = 'submit';
                    const result = await deps.submitTx({
                        instructions: built.instructions,
                        signers: built.signerKeys,
                    }, {
                        instructionName: leg.instructionName,
                        traceId: leg.legTraceId,
                        signingDeps,
                        activeWallet: params.activeWallet,
                        turnkeyDeps: deps.turnkeyDeps,
                        signal: abortController.signal,
                        slippageBps: params.slippageBps,
                        marketPhase: params.marketPhase,
                    });
                    if (result.ok) {
                        confirm.resolveFromTurnkey(result.signature);
                        return { landed: true, signature: result.signature, result, aborted: false };
                    }
                    if (abortController.signal.aborted) {
                        return { landed: false, signature: null, result, aborted: true };
                    }
                    // A definitive on-chain revert is authoritative — never let a stale
                    // NATS match keep it as success. Ambiguous post-send errors may still
                    // have landed → wait briefly for the indexer's trade event.
                    const isDefinitiveRevert = result.error.code === 'TX_CONFIRM_FAILED';
                    let landed = !isDefinitiveRevert && confirm.wasFired();
                    if (!landed && !isDefinitiveRevert && isAmbiguousLandingError(result.error.code)) {
                        landed = await confirm.awaitLanding(LANDING_GRACE_MS);
                    }
                    if (landed) {
                        const sig = confirm.firedSignature() ?? '';
                        return {
                            landed: true,
                            signature: sig,
                            result: { ok: true, signature: sig, traceId: leg.legTraceId },
                            aborted: false,
                        };
                    }
                    // Genuine failure — release the watch BEFORE the caller renders failure
                    // UX so a late NATS event cannot fire success on top of it.
                    confirm.release();
                    return { landed: false, signature: null, result, aborted: false };
                };
                // ── strategy 1: one atomic tx ──────────────────────────────────────────
                const oneTx = await executeLeg({
                    legTraceId: traceId,
                    instructionName: 'swap',
                    side: 'buy',
                    outcomeIndex: params.toOutcomeIndex,
                    startedAtMs: startedAt,
                    build: () => buildSwap(connection, {
                        market,
                        user,
                        fromOutcomeIndex: params.fromOutcomeIndex,
                        toOutcomeIndex: params.toOutcomeIndex,
                        fromTokensIn: params.tokensD6,
                        minUsdcOut,
                        buyAmountIn: params.buyAmountInD6,
                        minTokensOut,
                        payer: user,
                    }),
                    onLanded: fireSwapSuccess,
                    onReconcileSig: reShowSwapSuccess,
                });
                if (oneTx.landed) {
                    recordTradeEntityTarget(oneTx.signature, 2);
                    return withTradeEntityTargets({ ok: true, signature: oneTx.signature ?? '', traceId });
                }
                if (oneTx.aborted) {
                    if (!swapCompleted) {
                        showCancelledToast(deps, traceId);
                        deps.txSubmission.updateStage(traceId, 'cancelled');
                    }
                    return oneTx.result;
                }
                if (!oneTx.result.ok && oneTx.result.error.code !== 'TX_TOO_LARGE') {
                    // Genuine one-tx failure (a real revert / slippage / etc.) — surface it.
                    const err = oneTx.result.error;
                    trackTxFailure(deps, makeSwapCtx(), err);
                    deps.txSubmission.setError(traceId, {
                        code: err.code,
                        userMessageKey: err.userMessageKey,
                    });
                    showFailureToast(deps, traceId, err, (slippageBps) => {
                        retryWithSlippage(params, slippageBps);
                    });
                    return oneTx.result;
                }
                if (params.executionPolicy === 'atomic_only' && !oneTx.result.ok) {
                    const err = oneTx.result.error;
                    deps.logger.warn('[tx] atomic-only swap exceeds packet limit', {
                        traceId,
                        code: safeTxErrorCode(err.code),
                    });
                    trackTxFailure(deps, makeSwapCtx(), err);
                    deps.txSubmission.setError(traceId, {
                        code: err.code,
                        userMessageKey: err.userMessageKey,
                    });
                    showFailureToast(deps, traceId, err, (slippageBps) => {
                        retryWithSlippage(params, slippageBps);
                    });
                    return oneTx.result;
                }
                // ── strategy 2: two sequential txs (one-tx exceeded the packet limit) ──
                deps.logger.info('[tx] ⤳ one-tx swap exceeds packet limit — falling back to two txs', {
                    traceId,
                });
                // Leg 1 — SELL the `from` outcome.
                const sell = await executeLeg({
                    legTraceId: generateTraceId(),
                    instructionName: 'sell',
                    side: 'sell',
                    outcomeIndex: params.fromOutcomeIndex,
                    startedAtMs: Date.now(),
                    build: () => buildSell(connection, {
                        market,
                        outcomeIndex: params.fromOutcomeIndex,
                        user,
                        tokensIn: params.tokensD6,
                        minUsdcOut,
                        payer: user,
                    }),
                    onLanded: onSellLanded,
                    onReconcileSig: () => { },
                });
                if (sell.aborted) {
                    showCancelledToast(deps, traceId);
                    deps.txSubmission.updateStage(traceId, 'cancelled');
                    return sell.result;
                }
                if (!sell.landed) {
                    // The sell never landed → nothing changed on chain → the swap simply
                    // failed (no partial fill). Surface the sell's error.
                    if (!sell.result.ok) {
                        const err = sell.result.error;
                        trackTxFailure(deps, makeSwapCtx(), err);
                        deps.txSubmission.setError(traceId, {
                            code: err.code,
                            userMessageKey: err.userMessageKey,
                        });
                        showFailureToast(deps, traceId, err, (slippageBps) => {
                            retryWithSlippage(params, slippageBps);
                        });
                    }
                    return sell.result;
                }
                recordTradeEntityTarget(sell.signature, 1);
                // Leg 2 — BUY the `to` outcome with the realised proceeds. The sell has
                // landed (USDC credited on chain), so the buy budget (sized from the
                // sell's floored proceeds) is covered.
                const buy = await executeLeg({
                    legTraceId: generateTraceId(),
                    instructionName: 'buy',
                    side: 'buy',
                    outcomeIndex: params.toOutcomeIndex,
                    startedAtMs: Date.now(),
                    build: () => buildBuy(connection, {
                        market,
                        outcomeIndex: params.toOutcomeIndex,
                        user,
                        amountIn: params.buyAmountInD6,
                        minTokensOut,
                        payer: user,
                    }),
                    onLanded: onSwapComplete,
                    onReconcileSig: reShowSwapSuccess,
                });
                if (buy.landed) {
                    recordTradeEntityTarget(buy.signature, 1);
                    return withTradeEntityTargets({ ok: true, signature: buy.signature ?? '', traceId });
                }
                // PARTIAL FILL: the sell landed but the buy did not. The user holds USDC
                // (and no longer the `from` outcome). Surface this truthfully — NOT a
                // swap success — so the UI matches chain state and they can retry the buy.
                // The from-outcome was already patched down by `onSellLanded`; the
                // to-outcome is intentionally NOT patched up.
                if (!buy.result.ok) {
                    const buyErr = buy.result.error;
                    deps.logger.warn('[tx] swap partial fill — sold, but buy leg failed; funds are in USDC', {
                        traceId,
                        buyCode: safeTxErrorCode(buyErr.code),
                        aborted: buy.aborted,
                    });
                    const partialError = {
                        code: buyErr.code,
                        stage: 'confirming',
                        userMessageKey: 'tx.errors.swapPartialFill',
                        traceId,
                        recoveryAction: { type: 'prompt_retry' },
                    };
                    trackTxFailure(deps, makeSwapCtx(), partialError);
                    deps.txSubmission.setError(traceId, {
                        code: buyErr.code,
                        userMessageKey: 'tx.errors.swapPartialFill',
                    });
                    showFailureToast(deps, traceId, partialError);
                    return withTradeEntityTargets({ ok: false, error: partialError, traceId });
                }
                return withTradeEntityTargets(buy.result);
            }
            catch (err) {
                // Swap completed (buy / one-tx landed) before the throw → keep success.
                if (swapCompleted) {
                    deps.logger.warn('Swap threw after completing; keeping success', {
                        traceId,
                        errorClass: err instanceof Error ? 'Error' : typeof err,
                    });
                    return withTradeEntityTargets({
                        ok: true,
                        signature: completedSignature ?? '',
                        traceId,
                    });
                }
                if (abortController.signal.aborted) {
                    showCancelledToast(deps, traceId);
                    deps.txSubmission.updateStage(traceId, 'cancelled');
                    return withTradeEntityTargets({
                        ok: false,
                        error: {
                            code: 'TX_CANCELLED',
                            stage: 'preflight',
                            userMessageKey: 'tx.errors.cancelled',
                            traceId,
                            recoveryAction: { type: 'not_retryable' },
                        },
                        traceId,
                    });
                }
                // A throw AFTER the sell landed is a partial fill — the user holds USDC.
                const buildFailed = !sellLanded && stage === 'build';
                const code = buildFailed ? 'TX_BUILD_FAILED' : 'TX_UNKNOWN';
                const userMessageKey = sellLanded
                    ? 'tx.errors.swapPartialFill'
                    : buildFailed
                        ? 'tx.errors.buildFailed'
                        : 'tx.errors.unknown';
                deps.txSubmission.setError(traceId, { code, userMessageKey });
                showFailureToast(deps, traceId, {
                    code,
                    stage: 'preflight',
                    userMessageKey,
                    traceId,
                    recoveryAction: { type: 'prompt_retry' },
                });
                trackTxException(deps, makeSwapCtx(), err, code);
                reconcileTradeEntityTargets(deps, currentTradeEntityTargets());
                throw err;
            }
            finally {
                for (const confirm of confirms) {
                    confirm.release();
                }
            }
        },
        onSettled: (data, _error, params) => {
            const signature = data?.ok === true && data.signature.length > 0 ? data.signature : undefined;
            reconcileSettledTradeEntities(deps, data);
            reconcileThesisReplyEligibility(deps, params.marketAddress);
            deps.onMutationSettled('swap', {
                marketAddress: params.marketAddress,
                walletAddress: params.activeWallet.address,
                ...(signature === undefined ? {} : { signature }),
            });
        },
    });
    function retryWithSlippage(params, slippageBps) {
        mutation.mutate({ ...params, slippageBps });
    }
    return { ...mutation, abortControllerRef };
}
//# sourceMappingURL=use-swap-trade.js.map